import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { ChangeHistory } from '@/common/components/ChangeHistory'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import { FormTransformers } from '@/common/utils/formTransformers'
import { RequestedReview } from '@/features/establishmentLeadsFunnel/components/RequestedReview'
import { Checklist } from '@/features/privateCompanyLeadsFunnel/components/Checklist'
import { AddressSection } from '@/features/publicCompanyLeadsFunnel/components/AddressSection'
import { BiddingInformationSection } from '@/features/publicCompanyLeadsFunnel/components/BiddingInformationSection'
import { ContactsSection } from '@/features/publicCompanyLeadsFunnel/components/ContactsSection'
import { FiscalDocumentInformationSection } from '@/features/publicCompanyLeadsFunnel/components/FiscalDocumentInformationSection'
import { GeneralInformationSection } from '@/features/publicCompanyLeadsFunnel/components/GeneralInformationSection'
import { GenericDocumentsSection } from '@/features/publicCompanyLeadsFunnel/components/GenericDocumentsSection'
import { LeadHeader } from '@/features/publicCompanyLeadsFunnel/components/LeadHeader'
import { ResponsibleSection } from '@/features/publicCompanyLeadsFunnel/components/ResponsibleSection'
import { SpecialFees } from '@/features/publicCompanyLeadsFunnel/components/SpecialFees'
import {
  stateStrategy,
  translateStateStrategy,
} from '@/features/publicCompanyLeadsFunnel/constants/stateStrategy.constant'
import { useAdvancePublicCompanyLead } from '@/features/publicCompanyLeadsFunnel/hooks/useAdvancePublicCompanyLead'
import { useCreatePublicCompanyLeadSpecialFee } from '@/features/publicCompanyLeadsFunnel/hooks/useCreatePublicCompanyLeadSpecialFee'
import { useDeletePublicCompanyLeadSpecialFee } from '@/features/publicCompanyLeadsFunnel/hooks/useDeletePublicCompanyLeadSpecialFee'
import usePublicCompanyLeadForm from '@/features/publicCompanyLeadsFunnel/hooks/usePublicCompanyLeadForm'
import { useUpdatePublicCompanyLead } from '@/features/publicCompanyLeadsFunnel/hooks/useUpdatePublicCompanyLead'
import { useUpdatePublicCompanyLeadMasterUser } from '@/features/publicCompanyLeadsFunnel/hooks/useUpdatePublicCompanyLeadMasterUser'
import { PublicCompanyLeadsFunnelUpdateValidationSchema } from '@/features/publicCompanyLeadsFunnel/schemas/update.schema'
import { PublicCompanyLeadData } from '@/features/publicCompanyLeadsFunnel/types/publicCompanyLead'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { FormProvider, SubmitHandler } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useUpdatePublicCompanyLeadManagers } from '../../hooks/useUpdatePublicCompanyLeadManagers'

const LeadContractingForm = ({ id, publicCompanyLead }: { id: string; publicCompanyLead: PublicCompanyLeadData }) => {
  const queryClient = useQueryClient()
  const [file, setFile] = useState<Blob>()
  const navigate = useNavigate()
  const passedEveryRequirement = publicCompanyLead?.requirement_list_summary?.steps?.every((step) => step.passed)

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const updateLeadOnSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['publicCompanyLead', id] })
  }

  const redirectOnSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['publicCompanyLead', id] })
    navigate('/public-clients')
  }

  const {
    mutate: advancePublicCompanyLead,
    isPending: advancePublicCompanyLeadIsPending,
    isError: advancePublicCompanyLeadIsError,
  } = useAdvancePublicCompanyLead({ id, onSuccess: redirectOnSuccess })

  const {
    mutate: createPublicCompanyLeadSpecialFee,
    isPending: createPublicCompanyLeadSpecialFeeIsPending,
    isError: createPublicCompanyLeadSpecialFeeIsError,
  } = useCreatePublicCompanyLeadSpecialFee({
    publicCompanyLeadId: id,
    onSuccess: () => {
      setValue('fee_rule.special_entries', null)
      updateLeadOnSuccess()
    },
    onError: (error) => {
      setValue('fee_rule.special_entries', null);

      if (error.response?.data.errors)
        setMessages(Object.values(error.response?.data.errors).flat())
    },
  })

  const {
    mutate: deletePublicCompanyLeadSpecialFee,
    isPending: deletePublicCompanyLeadSpecialFeeIsPending,
    isError: deletePublicCompanyLeadSpecialFeeIsError,
  } = useDeletePublicCompanyLeadSpecialFee({
    publicCompanyLeadId: id,
    onSuccess: updateLeadOnSuccess,
  })

  const {
    mutate: updatePublicCompanyLeadMasterUser,
    isPending: updatePublicCompanyLeadMasterUserIsPending,
    isError: updatePublicCompanyLeadMasterUserIsError,
  } = useUpdatePublicCompanyLeadMasterUser({
    publicCompanyLeadId: id,
    onSuccess: updateLeadOnSuccess,
  })

  const useFormMethods = usePublicCompanyLeadForm({ publicCompanyLead })

  const { handleSubmit, setValue, clearErrors, setError, getValues } = useFormMethods

  const onSubmitChangeMasterUser: SubmitHandler<PublicCompanyLeadsFunnelUpdateValidationSchema> = (data) =>
    updatePublicCompanyLeadMasterUser(data)

  const submitChangeMasterUser = handleSubmit(onSubmitChangeMasterUser)

  const {
    mutate: updatePublicCompanyLead,
    isPending: updatePublicCompanyLeadIsPending,
    isError: updatePublicCompanyLeadIsError,
  } = useUpdatePublicCompanyLead({
    id,
    publicCompanyLead,
    file,
    onSuccess: updateLeadOnSuccess,
    onError: FormTransformers.errorsTransformer<PublicCompanyLeadsFunnelUpdateValidationSchema>({ setError }),
  })

  const {
    mutate: updatePublicCompanyLeadManagers,
    isPending: updatePublicCompanyLeadManagersIsPending,
  } = useUpdatePublicCompanyLeadManagers({
    id,
    onSuccess: updateLeadOnSuccess,
    onError: FormTransformers.errorsTransformer<PublicCompanyLeadsFunnelUpdateValidationSchema>({ setError }),
  });

  const submitManagers = async () => {
    updatePublicCompanyLeadManagers(getValues());
  };

  const onSubmit: SubmitHandler<PublicCompanyLeadsFunnelUpdateValidationSchema> = (data) => {
    clearErrors()
    updatePublicCompanyLead(data)
  }

  const submit = handleSubmit(onSubmit)

  const submittableOnBlur = (
    e: React.FocusEvent<HTMLInputElement, Element>,
    onBlur: (e: React.FocusEvent<HTMLInputElement, Element>) => void,
  ) => {
    if (e.target.value === '') {
      return
    }
    onBlur(e)
    submit()
  }

  const isServerPending =
    advancePublicCompanyLeadIsPending ||
    updatePublicCompanyLeadIsPending ||
    updatePublicCompanyLeadMasterUserIsPending ||
    createPublicCompanyLeadSpecialFeeIsPending ||
    deletePublicCompanyLeadSpecialFeeIsPending

  const isServerError =
    advancePublicCompanyLeadIsError ||
    updatePublicCompanyLeadIsError ||
    updatePublicCompanyLeadMasterUserIsError ||
    createPublicCompanyLeadSpecialFeeIsError ||
    deletePublicCompanyLeadSpecialFeeIsError

  return (
    <FormProvider {...useFormMethods}>
      <form>
        <LeadHeader
          publicCompanyLeadId={id}
          display_name={publicCompanyLead.display_name}
          url={publicCompanyLead.general_information?.company_logo?.url}
          showArchiveButton
          archived_at={publicCompanyLead.archived_at}
          buttons={
            <Button
              type="button"
              name="advance_lead"
              className={passedEveryRequirement ? 'shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]' : ''}
              variant={passedEveryRequirement ? 'default' : 'outline-disabled'}
              disabled={!passedEveryRequirement || isServerPending}
              onClick={() => advancePublicCompanyLead()}
              isLoading={advancePublicCompanyLeadIsPending}
            >
              Enviar para análise
            </Button>
          }
        />
        <div className="flex gap-6">
          <Box className="w-full flex flex-col gap-6 h-[calc(100vh-130px)] overflow-y-scroll">
            {isServerError && <p className="text-sm text-danger-soft">Ocorreu um erro no servidor, tente novamente</p>}
            {publicCompanyLead.last_review?.state === 'disapproved' && (
              <RequestedReview description={publicCompanyLead.last_review?.description} />
            )}
            <ResponsibleSection
              publicCompanyLead={publicCompanyLead}
              submit={submitManagers}
              submitObservations={submit}
              disabled={advancePublicCompanyLeadIsPending || updatePublicCompanyLeadManagersIsPending}
            />
            <BiddingInformationSection
              publicCompanyLeadId={id}
              publicCompanyLeadState={publicCompanyLead.state}
              contractId={publicCompanyLead.contract?.id}
              contractFile={publicCompanyLead.contract?.file}
              bidding_information={publicCompanyLead.bidding_information}
              disabled
            />
            <SpecialFees
              fee_rule={publicCompanyLead.fee_rule}
              createPublicCompanyLeadSpecialFee={createPublicCompanyLeadSpecialFee}
              createPublicCompanyLeadSpecialFeeIsPending={createPublicCompanyLeadSpecialFeeIsPending}
              deletePublicCompanyLeadSpecialFee={deletePublicCompanyLeadSpecialFee}
              disabled={isServerPending}
            />
            <FiscalDocumentInformationSection
              fiscal_document_information={publicCompanyLead.fiscal_document_information}
              submittableOnBlur={submittableOnBlur}
              submit={submit}
              disabled={isServerPending}
            />
            <GeneralInformationSection
              general_information={publicCompanyLead.general_information}
              file={file}
              setFile={setFile}
              disabled
            />
            <AddressSection address={publicCompanyLead.address} disabled />
            <ContactsSection
              publicCompanyLeadId={id}
              contacts={publicCompanyLead.contacts}
              leadState={publicCompanyLead.state}
              submit={submitChangeMasterUser}
              contact_for_master_user={publicCompanyLead.contact_for_master_user}
              updateLeadOnSuccess={updateLeadOnSuccess}
              disabled={isServerPending}
            />
            <GenericDocumentsSection
              publicCompanyLeadId={id}
              generic_documents={publicCompanyLead.generic_documents}
              disabled
            />
          </Box>
          <div className="flex flex-col gap-4">
            <Checklist
              requirement_list_summary={publicCompanyLead.requirement_list_summary}
              state={translateStateStrategy[stateStrategy[publicCompanyLead.state as keyof typeof stateStrategy]]}
              className="flex flex-col gap-2 justify-between h-[calc((100vh-276px)/2)]"
              Buttons={() => (
                <Button
                  type="button"
                  variant={passedEveryRequirement ? 'default' : 'outline-disabled'}
                  disabled={!passedEveryRequirement || isServerPending}
                  onClick={() => advancePublicCompanyLead()}
                  isLoading={advancePublicCompanyLeadIsPending}
                >
                  Enviar para análise
                </Button>
              )}
            />
            <ChangeHistory logs={publicCompanyLead?.logs || []} entityId={id} />
          </div>
        </div>
      </form>

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </FormProvider>
  )
}

export default LeadContractingForm
